import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Plus, Eye, Trash2, Pencil, Printer, Plane, Users as UsersIcon, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0 });
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SEAT_TYPES = [
  { v: 'adult_with_seat', label: 'كبير (بكرسي)', priceField: 'price_adult_with_seat' },
  { v: 'child_with_seat', label: 'طفل (بكرسي)', priceField: 'price_child_with_seat' },
  { v: 'child_no_seat', label: 'طفل (بدون كرسي)', priceField: 'price_child_no_seat' },
  { v: 'infant_no_seat', label: 'رضيع', priceField: 'price_infant_no_seat' },
  { v: 'discounted', label: '🎖️ مستفيد من تخفيض', priceField: 'price_discounted' },
  { v: 'crew_free', label: '🎁 طاقم (مجاني)', priceField: null },
];

const DISCOUNT_CATEGORIES = ['مجاهد', 'ابن شهيد', 'أخرى'];

const emptyFlight = {
  umrah_trip_id: '',
  airline_name: '', departure_airport: '', arrival_airport: '',
  departure_date: '', return_date: '',
  total_seats: 200, free_seats_count: 4,
  price_adult_with_seat: 0, price_adult_no_seat: 0,
  price_child_with_seat: 0, price_child_no_seat: 0,
  price_infant_with_seat: 0, price_infant_no_seat: 0,
  price_discounted: 0,
  discount_amount: 0,
  price_crew_free: 0,
  notes: '',
};

const FlightsManagement = () => {
  const { user } = useContext(AuthContext);
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyFlight);
  const [editingFlightId, setEditingFlightId] = useState(null);
  const [availableTrips, setAvailableTrips] = useState([]);
  const [selected, setSelected] = useState(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'general_accountant' || user?.role === 'general_manager';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/flights`, { headers: auth() });
      setFlights(r.data || []);
    } catch (e) {
      toast.error('فشل التحميل: ' + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, []);

  const loadAvailableTrips = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/flights/meta/available-umrah-trips`, { headers: auth() });
      setAvailableTrips(r.data || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 🆕 React to pending-intent from UmrahGroupManagement
  useEffect(() => {
    const createTripId = sessionStorage.getItem('pending-create-flight-trip-id');
    const openFlightId = sessionStorage.getItem('pending-open-flight-id');
    if (createTripId) {
      sessionStorage.removeItem('pending-create-flight-trip-id');
      (async () => {
        try {
          const r = await axios.get(`${API}/flights/meta/available-umrah-trips`, { headers: auth() });
          const list = r.data || [];
          setAvailableTrips(list);
          // Pre-select the trip if it's still available
          const t = list.find(x => x.id === createTripId);
          if (t) {
            setForm({
              ...emptyFlight,
              umrah_trip_id: t.id,
              departure_date: t.departure_date || '',
              return_date: t.return_date || '',
              total_seats: t.total_seats || 200,
            });
            setShowForm(true);
          } else {
            toast.info('هذا الفوج لم يعد متاحاً (قد يكون مرتبطاً بالفعل برحلة جوية)');
          }
        } catch (e) { toast.error('فشل تحميل الأفواج المتاحة'); }
      })();
    }
    if (openFlightId && flights.length > 0) {
      sessionStorage.removeItem('pending-open-flight-id');
      const f = flights.find(x => x.id === openFlightId);
      if (f) setSelected(f);
    }
  }, [flights.length]);

  const openCreate = () => {
    setEditingFlightId(null);
    setForm(emptyFlight);
    loadAvailableTrips();
    setShowForm(true);
  };

  const openEdit = (f) => {
    setEditingFlightId(f.id);
    setForm({
      umrah_trip_id: f.umrah_trip_id || '',
      airline_name: f.airline_name || '',
      departure_airport: f.departure_airport || '',
      arrival_airport: f.arrival_airport || '',
      departure_date: f.departure_date || '',
      return_date: f.return_date || '',
      total_seats: f.total_seats || 0,
      free_seats_count: f.free_seats_count || 0,
      price_adult_with_seat: f.price_adult_with_seat || 0,
      price_adult_no_seat: f.price_adult_no_seat || 0,
      price_child_with_seat: f.price_child_with_seat || 0,
      price_child_no_seat: f.price_child_no_seat || 0,
      price_infant_with_seat: f.price_infant_with_seat || 0,
      price_infant_no_seat: f.price_infant_no_seat || 0,
      price_discounted: f.price_discounted || 0,
      discount_amount: f.discount_amount || 0,
      price_crew_free: f.price_crew_free || 0,
      notes: f.notes || '',
      status: f.status || 'active',
    });
    setShowForm(true);
  };

  const create = async () => {
    if (!form.umrah_trip_id) {
      toast.error('❌ يجب اختيار الفوج المرتبط بالرحلة الجوية');
      return;
    }
    if (!form.airline_name || !form.departure_date || !form.total_seats) {
      toast.error('❌ شركة الطيران + تاريخ المغادرة + إجمالي المقاعد إلزامية');
      return;
    }
    try {
      await axios.post(`${API}/flights`, form, { headers: auth() });
      toast.success('✅ تم إنشاء الرحلة');
      setShowForm(false); setForm(emptyFlight); setEditingFlightId(null); load();
    } catch (e) {
      toast.error('فشل: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const update = async () => {
    if (!form.airline_name || !form.departure_date || !form.total_seats) {
      toast.error('❌ شركة الطيران + تاريخ المغادرة + إجمالي المقاعد إلزامية');
      return;
    }
    try {
      // exclude umrah_trip_id from the patch — locked in edit mode
      const { umrah_trip_id, ...patch } = form;
      await axios.patch(`${API}/flights/${editingFlightId}`, patch, { headers: auth() });
      toast.success('✏️ تم تحديث الرحلة');
      setShowForm(false); setForm(emptyFlight); setEditingFlightId(null); load();
    } catch (e) {
      toast.error('فشل: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const submit = () => editingFlightId ? update() : create();

  const del = async (f) => {
    if (!window.confirm(`حذف الرحلة ${f.flight_no}؟`)) return;
    try {
      await axios.delete(`${API}/flights/${f.id}`, { headers: auth() });
      toast.success('🗑️ تم الحذف'); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="p-6 space-y-6" dir="rtl" data-testid="flights-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-blue-900 flex items-center gap-2">
            <Plane className="h-7 w-7" /> إدارة رحلات الطيران
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            كل رحلة جوية مرتبطة بفوج. ركّاب الفوج يظهرون آلياً + إمكانية إضافة ركاب مباشرين (walk-in).
            تكلفة الطيران تُحسب من جدول الأسعار حسب نوع المقعد.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="flight-add-btn">
            <Plus className="h-4 w-4 ml-2" /> رحلة جديدة
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>قائمة الرحلات</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-8 text-gray-500">جاري التحميل…</div>
            : flights.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Plane className="h-12 w-12 mx-auto mb-2 opacity-30" />
                لا توجد رحلات بعد. ابدأ بإنشاء رحلة جديدة مرتبطة بفوج.
              </div>
            ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-right">رقم الرحلة</TableHead>
                <TableHead className="text-right">شركة الطيران</TableHead>
                <TableHead className="text-right">الفوج المرتبط</TableHead>
                <TableHead className="text-right">المسار</TableHead>
                <TableHead className="text-right">المغادرة</TableHead>
                <TableHead className="text-right">المقاعد</TableHead>
                <TableHead className="text-right">المجانية</TableHead>
                <TableHead className="text-right">المالية (طيران)</TableHead>
                <TableHead className="text-right">الحالة</TableHead>
                <TableHead className="text-right">إجراءات</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {flights.map(f => (
                  <TableRow key={f.id} data-testid={`flight-row-${f.id}`}>
                    <TableCell className="font-mono font-bold">{f.flight_no}</TableCell>
                    <TableCell>{f.airline_name}</TableCell>
                    <TableCell className="text-xs">
                      {f.umrah_trip ? (
                        <Badge className="bg-purple-100 text-purple-800 border border-purple-200">
                          {f.umrah_trip.name}
                        </Badge>
                      ) : <span className="text-rose-500">⚠️ بدون فوج (قديم)</span>}
                    </TableCell>
                    <TableCell className="text-xs">{f.departure_airport} → {f.arrival_airport}</TableCell>
                    <TableCell className="text-xs">{f.departure_date}</TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-800 border border-blue-200">
                        {f.reserved_seats}/{f.total_seats}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                        🎁 {f.crew_free_used}/{f.free_seats_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {(f.estimated_cost || 0) > 0 && (
                        <div className="text-blue-700" title="السعر التقديري الأولي (Charter) — قبل تعبئة الفوج">
                          تقديري: {fmt(f.estimated_cost)}
                        </div>
                      )}
                      <div title="السعر الفعلي بعد إدخال المعتمرين">فعلي: {fmt(f.total_cost)}</div>
                      <div className="text-emerald-700">مدفوع: {fmt(f.paid_to_airline)}</div>
                      <div className="text-rose-700">متبقي: {fmt(f.remaining_to_airline)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={f.status === 'active' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-gray-100 text-gray-700'}>
                        {f.status === 'active' ? 'نشطة' : f.status === 'closed' ? 'مغلقة' : 'مسودة'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setSelected(f)} data-testid={`flight-open-${f.id}`}>
                          <Eye className="h-3 w-3" />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="text-blue-600" onClick={() => openEdit(f)} data-testid={`flight-edit-${f.id}`} title="تعديل الرحلة">
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button size="sm" variant="outline" className="text-rose-600" onClick={() => del(f)} data-testid={`flight-delete-${f.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setEditingFlightId(null); } setShowForm(o); }}>
        <DialogContent dir="rtl" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFlightId ? '✏️ تعديل رحلة الطيران' : '➕ إنشاء رحلة طيران جديدة'}</DialogTitle>
            <DialogDescription>
              {editingFlightId
                ? 'يمكنك تعديل البيانات الأساسية والأسعار. الفوج المرتبط لا يمكن تغييره بعد الإنشاء.'
                : 'سيتم توليد رقم الرحلة تلقائياً (FL-YYYY-NNNN). كل رحلة ترتبط بفوج واحد فقط.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 bg-purple-50 border border-purple-200 rounded p-3">
              <Label className="text-purple-900 font-bold">
                الفوج المرتبط {editingFlightId ? '(مقفول — غير قابل للتغيير)' : '* (الرحلة المنظمة)'}
              </Label>
              {editingFlightId ? (
                <div className="w-full px-3 py-2 border rounded-md mt-1 bg-gray-100 text-gray-700 text-sm" data-testid="flight-form-umrah-trip-locked">
                  {(() => {
                    const cur = flights.find(x => x.id === editingFlightId);
                    return cur?.umrah_trip?.name || cur?.umrah_trip_id || '— غير محدد —';
                  })()}
                </div>
              ) : (
                <select
                  value={form.umrah_trip_id}
                  onChange={e => {
                    const t = availableTrips.find(x => x.id === e.target.value);
                    setForm({
                      ...form,
                      umrah_trip_id: e.target.value,
                      departure_date: t?.departure_date || form.departure_date,
                      return_date: t?.return_date || form.return_date,
                      total_seats: t?.total_seats || form.total_seats,
                    });
                  }}
                  className="w-full px-3 py-2 border rounded-md mt-1"
                  data-testid="flight-form-umrah-trip"
                >
                  <option value="">— اختر الفوج —</option>
                  {availableTrips.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name} • {t.departure_date} • {t.total_seats} مقعد
                    </option>
                  ))}
                </select>
              )}
              {!editingFlightId && availableTrips.length === 0 && (
                <div className="text-xs text-amber-700 mt-1">
                  ⚠️ لا توجد أفواج متاحة (كلها مرتبطة بالفعل أو لا يوجد فوج بعد).
                </div>
              )}
            </div>
            <div className="col-span-2"><Label>شركة الطيران *</Label><Input value={form.airline_name} onChange={e=>setForm({...form, airline_name: e.target.value})} data-testid="flight-form-airline" /></div>
            <div><Label>مطار المغادرة</Label><Input value={form.departure_airport} onChange={e=>setForm({...form, departure_airport: e.target.value})} /></div>
            <div><Label>مطار الوصول</Label><Input value={form.arrival_airport} onChange={e=>setForm({...form, arrival_airport: e.target.value})} /></div>
            <div><Label>تاريخ المغادرة *</Label><Input type="date" value={form.departure_date} onChange={e=>setForm({...form, departure_date: e.target.value})} data-testid="flight-form-departure" /></div>
            <div><Label>تاريخ العودة</Label><Input type="date" value={form.return_date} onChange={e=>setForm({...form, return_date: e.target.value})} /></div>
            <div><Label>إجمالي المقاعد *</Label><Input type="number" value={form.total_seats || ''} placeholder="0" onChange={e=>setForm({...form, total_seats: parseInt(e.target.value)||0})} data-testid="flight-form-seats" /></div>
            <div><Label>عدد المقاعد المجانية</Label><Input type="number" value={form.free_seats_count || ''} placeholder="0" onChange={e=>setForm({...form, free_seats_count: parseInt(e.target.value)||0})} data-testid="flight-form-free" /></div>
            <div className="col-span-2 border-t pt-3 mt-2">
              <h4 className="font-bold text-blue-900 mb-2">💰 جدول تكلفة الطيران (4 فئات حسب رموز IATA)</h4>
              <p className="text-xs text-gray-600 mb-2">السعر الذي تدفعه الوكالة لشركة الطيران لكل نوع مقعد. الحقل الفارغ = 0.</p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">كبير <span className="text-blue-600 font-mono">ADT</span></Label><Input type="number" value={form.price_adult_with_seat || ''} placeholder="0" onChange={e=>setForm({...form, price_adult_with_seat: parseFloat(e.target.value)||0})} /></div>
                <div><Label className="text-xs">طفل <span className="text-blue-600 font-mono">CHD</span></Label><Input type="number" value={form.price_child_with_seat || ''} placeholder="0" onChange={e=>setForm({...form, price_child_with_seat: parseFloat(e.target.value)||0})} /></div>
                <div>
                  <Label className="text-xs">رضيع بكرسي <span className="text-blue-600 font-mono">INS</span></Label>
                  <Input type="number" value={form.price_infant_with_seat || ''} placeholder="0" onChange={e=>setForm({...form, price_infant_with_seat: parseFloat(e.target.value)||0})} title="متى نستخدم INS؟ عندما يكون في نفس العائلة رضيعان — الأول يجلس في الحضن (INF) والثاني يحتاج كرسياً مستقلاً (INS)." />
                  <p className="text-[10px] text-blue-600 mt-1">💡 يُستخدم للرضيع الثاني في العائلة (الأول في الحضن، الثاني بكرسي مستقل)</p>
                </div>
                <div><Label className="text-xs">رضيع بدون كرسي <span className="text-blue-600 font-mono">INF</span></Label><Input type="number" value={form.price_infant_no_seat || ''} placeholder="0" onChange={e=>setForm({...form, price_infant_no_seat: parseFloat(e.target.value)||0})} /></div>
              </div>
            </div>
            <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-3 mt-2">
              <h4 className="font-bold text-emerald-900 mb-1">🎖️ سعر المستفيدين من تخفيض الطيران</h4>
              <p className="text-xs text-emerald-700 mb-2">
                سعر موحّد للمجاهدين، أبناء الشهداء، وأي فئة أخرى مستفيدة من تخفيض شركة الطيران.
                أدخل <strong>أحد الحقلين</strong> فقط — الآخر يُحسب تلقائياً.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">💰 قيمة التخفيض (دج)</Label>
                  <Input
                    type="number"
                    value={form.discount_amount || ''}
                    onChange={e => {
                      const da = parseFloat(e.target.value) || 0;
                      const base = parseFloat(form.price_adult_with_seat) || 0;
                      setForm({
                        ...form,
                        discount_amount: da,
                        price_discounted: base > 0 ? Math.max(0, base - da) : form.price_discounted,
                      });
                    }}
                    data-testid="flight-form-discount-amount"
                    placeholder="مثال: 20000"
                  />
                  <p className="text-[10px] text-emerald-600 mt-1">المبلغ الذي يُخصم من سعر التذكرة العادية</p>
                </div>
                <div>
                  <Label className="text-xs">🎫 السعر بعد التخفيض (دج)</Label>
                  <Input
                    type="number"
                    value={form.price_discounted || ''}
                    onChange={e => {
                      const pd = parseFloat(e.target.value) || 0;
                      const base = parseFloat(form.price_adult_with_seat) || 0;
                      setForm({
                        ...form,
                        price_discounted: pd,
                        discount_amount: base > 0 ? Math.max(0, base - pd) : form.discount_amount,
                      });
                    }}
                    data-testid="flight-form-price-discounted"
                    placeholder="مثال: 80000"
                  />
                  <p className="text-[10px] text-emerald-600 mt-1">السعر النهائي للتذكرة بعد التخفيض</p>
                </div>
              </div>
            </div>
            <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-3 mt-2">
              <h4 className="font-bold text-amber-900 mb-1">🎁 سعر مقعد الطاقم</h4>
              <p className="text-xs text-amber-700 mb-2">
                المقاعد المجانية ليست بالضرورة 0 دج — أدخل التكلفة الفعلية التي تدفعها الوكالة
                لشركة الطيران لكل مرشد/طبيب/مرافق (الرسوم، التأمين، إلخ).
              </p>
              <div>
                <Label className="text-xs">سعر مقعد الطاقم (دج)</Label>
                <Input
                  type="number"
                  value={form.price_crew_free || ''}
                  placeholder="0"
                  onChange={e => setForm({...form, price_crew_free: parseFloat(e.target.value) || 0})}
                  data-testid="flight-form-price-crew"
                />
              </div>
            </div>
            <div className="col-span-2"><Label>ملاحظات</Label><Input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} /></div>
            {editingFlightId && (
              <div className="col-span-2 bg-slate-50 border border-slate-200 rounded p-3">
                <Label className="font-bold">حالة الرحلة</Label>
                <select
                  value={form.status || 'active'}
                  onChange={e => setForm({...form, status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md mt-1"
                  data-testid="flight-form-status"
                >
                  <option value="active">🟢 نشطة</option>
                  <option value="closed">🔒 مغلقة</option>
                  <option value="draft">📝 مسودة</option>
                  <option value="cancelled">❌ ملغاة</option>
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingFlightId(null); }}>إلغاء</Button>
            <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="flight-form-submit">
              {editingFlightId ? '💾 حفظ التعديلات' : 'إنشاء الرحلة'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selected && (
        <FlightDetail
          flight={selected}
          onClose={() => { setSelected(null); load(); }}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

// ============================================================ Flight Detail
const FlightDetail = ({ flight, onClose, isAdmin }) => {
  const [details, setDetails] = useState(null);
  const [manifest, setManifest] = useState([]);
  const [airlinePays, setAirlinePays] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [subBlocks, setSubBlocks] = useState([]);
  const [showWalkin, setShowWalkin] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [editBlock, setEditBlock] = useState(null);
  const [deleteBlockState, setDeleteBlockState] = useState(null);
  const [detailBlock, setDetailBlock] = useState(null);
  const [payBlock, setPayBlock] = useState(null);
  const [payWalkin, setPayWalkin] = useState(null);
  const [detailWalkin, setDetailWalkin] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [d, m, ap, ms, blocks] = await Promise.all([
        axios.get(`${API}/flights/${flight.id}`, { headers: auth() }),
        axios.get(`${API}/flights/${flight.id}/manifest`, { headers: auth() }),
        axios.get(`${API}/flights/${flight.id}/airline-payments`, { headers: auth() }),
        axios.get(`${API}/flights/${flight.id}/payment-milestones`, { headers: auth() }),
        axios.get(`${API}/flights/${flight.id}/subagent-blocks`, { headers: auth() }),
      ]);
      setDetails(d.data); setManifest(m.data.rows || []); setAirlinePays(ap.data || []); setMilestones(ms.data || []); setSubBlocks(blocks.data || []);
    } catch (e) { console.error(e); toast.error('فشل التحميل'); }
  }, [flight.id]);

  useEffect(() => { reload(); }, [reload]);

  const print = () => {
    const token = localStorage.getItem('token');
    fetch(`${API}/flights/${flight.id}/print`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.text()).then(html => {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      });
  };

  const delWalkin = async (p) => {
    if (!window.confirm(`حذف الراكب المباشر ${p.full_name}؟`)) return;
    try {
      await axios.delete(`${API}/flights/${flight.id}/walkins/${p.id}`, { headers: auth() });
      toast.success('🗑️ تم الحذف'); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const delPay = async (pay) => {
    if (!window.confirm(`حذف دفعة ${fmt(pay.amount)} دج؟ سيتم إرجاع المبلغ للخزينة.`)) return;
    try {
      const res = await axios.delete(`${API}/flights/${flight.id}/airline-payments/${pay.id}`, { headers: auth() });
      const newBal = res.data?.new_cashbox_balance;
      toast.success(
        `🗑️ تم حذف الدفعة${newBal !== undefined ? ` — الرصيد الجديد للخزينة: ${Number(newBal).toLocaleString()} دج` : ''}`,
      );
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const delMilestone = async (m) => {
    if (!window.confirm(`حذف الدفعة المجدولة "${m.label}" (${fmt(m.amount)} دج)؟`)) return;
    try {
      await axios.delete(`${API}/flights/payment-milestones/${m.id}`, { headers: auth() });
      toast.success('🗑️ تم الحذف'); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const markMilestonePaid = async (m) => {
    if (!window.confirm(`💰 تأكيد تسديد الدفعة «${m.label}» بقيمة ${fmt(m.amount)} دج؟\n\nسيتم خصم المبلغ من الخزينة المركزية فوراً وتسجيل العملية في سجل الدفعات.`)) return;
    try {
      const res = await axios.post(
        `${API}/flights/payment-milestones/${m.id}/settle`,
        { method: 'cash', reference: m.label, notes: `تسديد دفعة مجدولة — ${m.label}` },
        { headers: auth() },
      );
      const repaired = res.data?.repaired;
      const newBal = res.data?.new_cashbox_balance;
      toast.success(
        repaired
          ? `🛠️ تمت تسوية الخزينة لهذه الدفعة — الرصيد الجديد: ${fmt(newBal)} دج`
          : `✅ تم تسديد الدفعة وخصم ${fmt(res.data?.settled_amount || m.amount)} دج من الخزينة (الرصيد: ${fmt(newBal)} دج)`,
      );
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const actuallyDeleteBlock = async (b) => {
    try {
      const res = await axios.delete(`${API}/flights/subagent-blocks/${b.id}`, { headers: auth() });
      const revAmt = res.data?.reversed_amount || 0;
      toast.success(
        `🗑️ تم حذف الحجز${revAmt > 0 ? ` — تم استرجاع ${fmt(revAmt)} دج من الخزينة` : ''}`,
      );
      setDeleteBlockState(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const stats = details?.stats;
  const trip = details?.umrah_trip;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between flex-wrap gap-2">
            <span className="flex items-center gap-2">
              <Plane className="h-5 w-5" /> {flight.flight_no} — {flight.airline_name}
            </span>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={print} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="flight-print">
                <Printer className="h-3 w-3 ml-1" /> طباعة
              </Button>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowPay(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="airline-pay-btn">
                  <Wallet className="h-3 w-3 ml-1" /> دفعة للطيران
                </Button>
              )}
              <Button size="sm" onClick={() => setShowWalkin(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="walkin-add-btn">
                <Plus className="h-3 w-3 ml-1" /> راكب مباشر
              </Button>
            </div>
          </DialogTitle>
          <DialogDescription>
            {flight.departure_airport} → {flight.arrival_airport} • {flight.departure_date}
            {trip && <> • الفوج: <strong className="text-purple-700">{trip.name}</strong></>}
          </DialogDescription>
        </DialogHeader>

        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="flight-stats">
            <Card className="bg-blue-50"><CardContent className="p-3"><div className="text-xs text-gray-600">المقاعد المحجوزة</div><div className="text-lg font-bold text-blue-700">{stats.reserved_seats}/{stats.total_seats}</div></CardContent></Card>
            <Card className="bg-emerald-50"><CardContent className="p-3"><div className="text-xs text-gray-600">المقاعد المتبقية</div><div className="text-lg font-bold text-emerald-700">{stats.remaining_seats}</div></CardContent></Card>
            <Card className="bg-amber-50"><CardContent className="p-3"><div className="text-xs text-gray-600">🎁 المجانية</div><div className="text-lg font-bold text-amber-700">{stats.crew_free_used}/{stats.free_seats_count}</div></CardContent></Card>
            <Card className="bg-sky-50"><CardContent className="p-3"><div className="text-xs text-gray-600">👶 رضع</div><div className="text-lg font-bold text-sky-700">{(stats.ins_count || 0)} <span className="text-xs font-normal text-sky-600">INS</span> + {(stats.inf_count || 0)} <span className="text-xs font-normal text-sky-600">INF</span></div></CardContent></Card>
          </div>
        )}

        {/* Airline financials */}
        {stats && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Wallet className="h-4 w-4" /> مالية شركة الطيران</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="bg-blue-50 p-2 rounded border border-blue-200" title="السعر التقديري الأولي (Charter) — ما تتوقع دفعه قبل تعبئة الفوج بالكامل">
                  <div className="text-xs text-blue-700 font-semibold">💼 تقديري (Charter)</div>
                  <div className="font-bold text-blue-700">{fmt(stats.estimated_cost || 0)} دج</div>
                  <div className="text-[10px] text-blue-600">قبل تعبئة الفوج</div>
                </div>
                <div className="bg-teal-50 p-2 rounded border border-teal-200" title="السعر الفعلي محسوب من المعتمرين المُدخَلين فعلاً">
                  <div className="text-xs text-teal-700 font-semibold">📊 فعلي (لحظي)</div>
                  <div className="font-bold text-teal-700">{fmt(stats.total_cost)} دج</div>
                  <div className="text-[10px] text-teal-600">بعد إدخال المعتمرين</div>
                </div>
                <div className="bg-emerald-50 p-2 rounded"><div className="text-xs text-gray-500">المدفوع للطيران</div><div className="font-bold text-emerald-700">{fmt(stats.paid_to_airline)} دج</div></div>
                <div className="bg-amber-50 p-2 rounded"><div className="text-xs text-gray-500">المتبقي للطيران</div><div className="font-bold text-amber-700">{fmt(stats.remaining_to_airline)} دج</div></div>
              </div>
              {airlinePays.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 mb-1">سجل الدفعات:</div>
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead className="text-right">التاريخ</TableHead>
                      <TableHead className="text-right">المبلغ</TableHead>
                      <TableHead className="text-right">الطريقة</TableHead>
                      <TableHead className="text-right">المرجع</TableHead>
                      <TableHead className="text-right">ملاحظات</TableHead>
                      <TableHead className="text-right">سجَّل</TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {airlinePays.map(p => (
                        <TableRow key={p.id}>
                          <TableCell className="text-xs">{(p.payment_date || '').slice(0, 10)}</TableCell>
                          <TableCell className="font-mono font-bold text-emerald-700">{fmt(p.amount)} دج</TableCell>
                          <TableCell className="text-xs">{p.method}</TableCell>
                          <TableCell className="text-xs">
                            {p.reference || '—'}
                            {p.reference_document_url && (
                              <a
                                href={`${API}${p.reference_document_url}?token=${localStorage.getItem('token')}`}
                                target="_blank" rel="noopener noreferrer"
                                className="block text-blue-700 underline hover:text-blue-900 mt-0.5"
                                data-testid={`pay-doc-${p.id}`}
                                title="عرض وثيقة الدفع"
                                onClick={e => e.stopPropagation()}
                              >📎 الوثيقة</a>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{p.notes || '—'}</TableCell>
                          <TableCell className="text-xs">{p.created_by_name}</TableCell>
                          <TableCell>
                            {isAdmin && (
                              <Button size="sm" variant="outline" className="text-rose-600" onClick={() => delPay(p)} data-testid={`pay-del-${p.id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 🆕 Payment Milestones (دفعات الطيران المجدولة) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
              <span className="flex items-center gap-2">
                <span>📅</span> دفعات الطيران المجدولة ({milestones.length})
              </span>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowMilestone(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="milestone-add-btn">
                  <Plus className="h-3 w-3 ml-1" /> إضافة دفعة مجدولة
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {milestones.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">
                لا توجد دفعات مجدولة بعد. أضف القيود التي عاطيك إياها شركة الطيران (أولى، ثانية، نهائية…) لتذكير السستم بها قبل موعدها.
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-right">الدفعة</TableHead>
                  <TableHead className="text-right">القيمة</TableHead>
                  <TableHead className="text-right">تاريخ الاستحقاق</TableHead>
                  <TableHead className="text-right">المسدد</TableHead>
                  <TableHead className="text-right">المتبقي</TableHead>
                  <TableHead className="text-right">الحالة</TableHead>
                  <TableHead className="text-right">ملاحظات</TableHead>
                  <TableHead className="text-right">إجراء</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {milestones.map(m => {
                    const remaining = Math.max(0, (m.amount || 0) - (m.paid_amount || 0));
                    const rs = m.runtime_status || m.status || 'pending';
                    const badgeCls =
                      rs === 'paid' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                      rs === 'partial' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                      rs === 'overdue' ? 'bg-rose-100 text-rose-800 border-rose-200' :
                      'bg-amber-100 text-amber-800 border-amber-200';
                    const badgeLabel =
                      rs === 'paid' ? '✅ مسددة' :
                      rs === 'partial' ? '⏳ جزئية' :
                      rs === 'overdue' ? '⚠️ متأخرة' :
                      '🕒 معلّقة';
                    return (
                      <TableRow key={m.id} data-testid={`milestone-row-${m.id}`}>
                        <TableCell className="font-semibold">{m.label}</TableCell>
                        <TableCell className="font-mono">{fmt(m.amount)} دج</TableCell>
                        <TableCell className="text-xs">{m.due_date}</TableCell>
                        <TableCell className="text-emerald-700 font-mono">{fmt(m.paid_amount || 0)}</TableCell>
                        <TableCell className="text-rose-700 font-mono">{fmt(remaining)}</TableCell>
                        <TableCell><Badge className={`border ${badgeCls}`}>{badgeLabel}</Badge></TableCell>
                        <TableCell className="text-xs">{m.notes || '—'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {isAdmin && rs !== 'paid' && (
                              <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => markMilestonePaid(m)} data-testid={`milestone-paid-${m.id}`} title="تأكيد تسديد كامل المبلغ">
                                ✅
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" className="text-rose-600" onClick={() => delMilestone(m)} data-testid={`milestone-del-${m.id}`}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* 🆕 Subagent Block Bookings (حجوزات الوكالات الشريكة) */}
        <Card className="border-violet-300">
          <CardHeader className="pb-2 bg-gradient-to-l from-violet-50 to-white">
            <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
              <span className="flex items-center gap-2 text-violet-900">
                <span>🏢</span> حجوزات الوكالات الشريكة (Block Bookings) — {subBlocks.length}
              </span>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowBlock(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="block-add-btn">
                  <Plus className="h-3 w-3 ml-1" /> حجز كتلة جديدة
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subBlocks.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">
                لا توجد حجوزات وكالات شريكة. اضغط «حجز كتلة جديدة» لتسجيل عدد المقاعد المخصصة لوكالة شريكة (يمكن إضافة الأسماء لاحقاً إن وُجدت).
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-right">الوكالة الشريكة</TableHead>
                  <TableHead className="text-right">المقاعد</TableHead>
                  <TableHead className="text-right">السعر الإجمالي</TableHead>
                  <TableHead className="text-right">المدفوع</TableHead>
                  <TableHead className="text-right">الباقي</TableHead>
                  <TableHead className="text-right">التكلفة (طيران)</TableHead>
                  <TableHead className="text-right">الأسماء</TableHead>
                  <TableHead className="text-right">إجراء</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {subBlocks.map(b => {
                    const remaining = b.amount_remaining ?? ((b.total_price || 0) - (b.amount_paid || 0));
                    const isPaid = remaining <= 0.01;
                    return (
                      <TableRow key={b.id} data-testid={`block-row-${b.id}`}>
                        <TableCell className="font-semibold text-violet-800">
                          🏢 {b.subagent_name}
                          {b.notes && <div className="text-[10px] text-gray-500 font-normal mt-0.5">{b.notes}</div>}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="flex flex-wrap gap-1">
                            {b.seats_adult > 0 && <Badge className="bg-blue-100 text-blue-800">ADT×{b.seats_adult}</Badge>}
                            {b.seats_child > 0 && <Badge className="bg-emerald-100 text-emerald-800">CHD×{b.seats_child}</Badge>}
                            {b.seats_infant_with_seat > 0 && <Badge className="bg-sky-100 text-sky-800">INS×{b.seats_infant_with_seat}</Badge>}
                            {b.seats_infant_no_seat > 0 && <Badge className="bg-cyan-100 text-cyan-800">INF×{b.seats_infant_no_seat}</Badge>}
                          </div>
                          <div className="text-[10px] text-gray-600 mt-1">إجمالي: {b.seats_total} مقعد</div>
                        </TableCell>
                        <TableCell className="font-mono text-blue-700">{fmt(b.total_price)} دج</TableCell>
                        <TableCell className="font-mono text-emerald-700">{fmt(b.amount_paid)} دج</TableCell>
                        <TableCell className="font-mono text-rose-700">{fmt(remaining)} دج</TableCell>
                        <TableCell className="font-mono text-xs text-amber-700">{fmt(b.block_cost_snapshot)}</TableCell>
                        <TableCell className="text-xs">
                          {(b.names_count || 0) > 0 ? `${b.names_count} اسم` : <span className="text-gray-400">بدون أسماء</span>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => setDetailBlock(b)} data-testid={`block-detail-${b.id}`} title="عرض كل التفاصيل وسجل الدفعات">
                              <Eye className="h-3 w-3" />
                            </Button>
                            {isAdmin && !isPaid && (
                              <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => setPayBlock(b)} data-testid={`block-pay-${b.id}`} title="تسجيل دفعة">
                                💰
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" className="text-blue-700" onClick={() => setEditBlock(b)} data-testid={`block-edit-${b.id}`} title="تعديل الحجز">
                                <Pencil className="h-3 w-3" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" className="text-rose-600" onClick={() => setDeleteBlockState(b)} data-testid={`block-del-${b.id}`} title="حذف الحجز">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Manifest */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <UsersIcon className="h-4 w-4" /> قائمة الركاب ({manifest.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-right">#</TableHead>
                <TableHead className="text-right">المصدر</TableHead>
                <TableHead className="text-right">الاسم</TableHead>
                <TableHead className="text-right">الجواز/CIN</TableHead>
                <TableHead className="text-right">نوع المقعد</TableHead>
                <TableHead className="text-right">الدور</TableHead>
                <TableHead className="text-right">التكلفة (طيران)</TableHead>
                <TableHead className="text-right">سعر البيع للعميل</TableHead>
                <TableHead className="text-right">المدفوع للوكالة</TableHead>
                <TableHead className="text-right">إجراء</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {manifest.map((p, i) => (
                  <TableRow key={`${p.source}-${p.id}`} data-testid={`pax-row-${p.id}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <Badge className={p.source === 'walkin' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800'}>
                        {p.source === 'walkin' ? 'walk-in' : 'فوج'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {p.full_name}
                      {p.source === 'walkin' && p.buyer_type === 'subagent' && (
                        <div className="text-[10px] text-violet-700 font-semibold mt-0.5">
                          🏢 {p.subagent_name || '—'}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.passport_no || p.cin || '—'}</TableCell>
                    <TableCell>
                      <Badge className={
                        p.seat_type === 'crew_free' ? 'bg-amber-100 text-amber-800' :
                        p.seat_type === 'discounted' ? 'bg-emerald-100 text-emerald-800' :
                        'bg-blue-100 text-blue-800'
                      }>
                        {p.seat_label || p.seat_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {p.crew_role || p.discount_category || '—'}
                      {p.discount_document_url && (
                        <a
                          href={`${API}${p.discount_document_url}?token=${localStorage.getItem('token')}`}
                          target="_blank" rel="noopener noreferrer"
                          className="block text-emerald-700 underline hover:text-emerald-900 mt-0.5"
                          data-testid={`pax-discount-doc-${p.id}`}
                          title="عرض سند التخفيض"
                          onClick={e => e.stopPropagation()}
                        >📎 السند</a>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{fmt(p.unit_cost)}</TableCell>
                    <TableCell className="font-mono text-blue-700">{p.sale_price ? fmt(p.sale_price) : '—'}</TableCell>
                    <TableCell className="font-mono">
                      {p.sale_paid > 0 ? (
                        <div>
                          <div className="text-emerald-700">{fmt(p.sale_paid)}</div>
                          {p.sale_remaining > 0 && (
                            <div className="text-[10px] text-rose-600">باقي: {fmt(p.sale_remaining)}</div>
                          )}
                        </div>
                      ) : (p.sale_price > 0 ? <span className="text-rose-600 text-xs">على دين</span> : '—')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {p.source === 'walkin' && isAdmin && p.sale_remaining > 0 && (
                          <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => setPayWalkin(p)} data-testid={`walkin-pay-${p.id}`} title="تسجيل دفعة">
                            💰
                          </Button>
                        )}
                        {p.source === 'walkin' && (
                          <Button size="sm" variant="outline" className="text-blue-700" onClick={() => setDetailWalkin(p)} data-testid={`walkin-details-${p.id}`} title="تفاصيل الدفعات والوصولات">
                            👁️
                          </Button>
                        )}
                        {p.source === 'walkin' && isAdmin && (
                          <Button size="sm" variant="outline" className="text-rose-600" onClick={() => delWalkin(p)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {manifest.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center text-gray-400 py-6">لا يوجد ركاب بعد</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <div className="text-xs text-gray-500 mt-2">
              ℹ️ ركّاب الفوج محسوبون حياً من نظام إدارة الأفواج (إضافة/تعديل المعتمر تتم من تبويب الأفواج).
              فقط الركاب المباشرين (walk-in) يمكن حذفهم هنا.
            </div>
          </CardContent>
        </Card>

        {showWalkin && (
          <AddWalkinDialog
            flight={details || flight}
            onClose={() => setShowWalkin(false)}
            onAdded={() => { setShowWalkin(false); reload(); }}
          />
        )}
        {showPay && (
          <AddAirlinePaymentDialog
            flight={flight}
            onClose={() => setShowPay(false)}
            onAdded={() => { setShowPay(false); reload(); }}
          />
        )}
        {showMilestone && (
          <AddMilestoneDialog
            flight={flight}
            onClose={() => setShowMilestone(false)}
            onAdded={() => { setShowMilestone(false); reload(); }}
          />
        )}
        {showBlock && (
          <AddSubagentBlockDialog
            flight={flight}
            details={details}
            onClose={() => setShowBlock(false)}
            onAdded={() => { setShowBlock(false); reload(); }}
          />
        )}
        {editBlock && (
          <EditSubagentBlockDialog
            block={editBlock}
            flight={flight}
            details={details}
            onClose={() => setEditBlock(null)}
            onSaved={() => { setEditBlock(null); reload(); }}
          />
        )}
        {deleteBlockState && (
          <ConfirmDeleteBlockDialog
            block={deleteBlockState}
            onClose={() => setDeleteBlockState(null)}
            onConfirm={() => actuallyDeleteBlock(deleteBlockState)}
          />
        )}
        {detailBlock && (
          <SubagentBlockDetailsDialog
            block={detailBlock}
            flight={flight}
            onClose={() => setDetailBlock(null)}
            onPay={() => { setPayBlock(detailBlock); setDetailBlock(null); }}
          />
        )}
        {payBlock && (
          <PaySubagentBlockDialog
            block={payBlock}
            onClose={() => setPayBlock(null)}
            onPaid={() => { setPayBlock(null); reload(); }}
          />
        )}
        {payWalkin && (
          <PayWalkinDialog
            flight={flight}
            walkin={payWalkin}
            onClose={() => setPayWalkin(null)}
            onPaid={() => { setPayWalkin(null); reload(); }}
          />
        )}
        {detailWalkin && (
          <WalkinPaymentsDialog
            flight={flight}
            walkin={detailWalkin}
            onClose={() => setDetailWalkin(null)}
            onPay={() => { setPayWalkin(detailWalkin); setDetailWalkin(null); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Pay Walk-in (B2B installments / late receipts)
const PayWalkinDialog = ({ flight, walkin, onClose, onPaid }) => {
  const [form, setForm] = useState({ amount: 0, method: 'cash', reference: '', notes: '' });
  const [docFile, setDocFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const remaining = (walkin.sale_price || 0) - (walkin.sale_paid || 0);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setDocFile(null); return; }
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(f.type)) {
      toast.error('❌ السماح فقط بـ JPG / PNG / PDF');
      e.target.value = ''; setDocFile(null); return;
    }
    if (f.size > 4 * 1024 * 1024) {
      toast.error('❌ الملف أكبر من 4 ميغا'); e.target.value = ''; setDocFile(null); return;
    }
    setDocFile(f);
  };

  const submit = async () => {
    const amt = parseFloat(form.amount) || 0;
    if (amt <= 0) { toast.error('❌ المبلغ يجب أن يكون موجباً'); return; }
    if (amt > remaining + 0.01) { toast.error(`❌ المبلغ يتجاوز الباقي (${fmt(remaining)} دج)`); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/flights/${flight.id}/walkins/${walkin.id}/pay`,
        { ...form, amount: amt },
        { headers: auth() },
      );
      const profitDelta = res.data?.profit_delta || 0;
      const paymentId = res.data?.payment_id;
      // 🆕 ارفع وصل الإثبات إن وُجد
      if (docFile && paymentId) {
        const fd = new FormData();
        fd.append('document', docFile);
        try {
          await axios.post(
            `${API}/flights/walkins/payments/${paymentId}/upload-reference`,
            fd,
            { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } },
          );
        } catch (upErr) {
          toast.error('⚠️ الدفعة سُجلت لكن فشل رفع الوثيقة: ' + (upErr?.response?.data?.detail || upErr.message));
        }
      }
      toast.success(
        `✅ تم تسجيل دفعة ${fmt(amt)} دج في الخزينة${profitDelta > 0 ? ` • ربح صافٍ: ${fmt(profitDelta)} دج` : ''}${docFile ? ' • مع وثيقة الإثبات' : ''}`,
      );
      onPaid();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-md">
        <DialogHeader>
          <DialogTitle>💰 تسجيل دفعة على تذكرة walk-in</DialogTitle>
          <DialogDescription>
            {walkin.full_name}
            {walkin.buyer_type === 'subagent' && walkin.subagent_name && (
              <span className="block text-violet-700 mt-1">🏢 وكالة شريكة: {walkin.subagent_name}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-gray-50 rounded p-2 text-xs grid grid-cols-3 gap-2">
            <div><div className="text-gray-500">سعر البيع</div><div className="font-bold text-blue-700">{fmt(walkin.sale_price)} دج</div></div>
            <div><div className="text-gray-500">المدفوع سابقاً</div><div className="font-bold text-emerald-700">{fmt(walkin.sale_paid || 0)} دج</div></div>
            <div><div className="text-gray-500">الباقي</div><div className="font-bold text-rose-700">{fmt(remaining)} دج</div></div>
          </div>
          <div>
            <Label>المبلغ (دج) *</Label>
            <Input
              type="number"
              value={form.amount || ''}
              placeholder="0"
              onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})}
              data-testid="walkin-pay-amount"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>طريقة الدفع</Label>
              <select
                value={form.method}
                onChange={e => setForm({...form, method: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="cash">نقداً</option>
                <option value="transfer">حوالة</option>
                <option value="check">شيك</option>
                <option value="card">بطاقة</option>
              </select>
            </div>
            <div>
              <Label>رقم المرجع</Label>
              <Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} placeholder="REC-..." />
            </div>
          </div>
          <div>
            <Label>📎 وثيقة الإثبات (اختياري)</Label>
            <Input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              onChange={onPickFile}
              data-testid="walkin-pay-document"
              className="cursor-pointer"
            />
            <p className="text-[10px] text-gray-500 mt-1">JPG / PNG / PDF — حتى 4 ميغا. ستظهر الوثيقة في سجل الدفعات للمراجعة لاحقاً.</p>
            {docFile && (
              <div className="mt-1 text-xs bg-blue-50 border border-blue-200 rounded p-1.5 text-blue-800">
                ✅ مُختار: <strong>{docFile.name}</strong> ({(docFile.size / 1024).toFixed(0)} KB)
              </div>
            )}
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="walkin-pay-submit">
            {submitting ? '⏳ جارٍ الحفظ…' : '💾 تسجيل الدفعة'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Walk-in Payments History + Receipt Upload
const WalkinPaymentsDialog = ({ flight, walkin, onClose, onPay }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/flights/${flight.id}/walkins/${walkin.id}/payments`,
        { headers: auth() },
      );
      setPayments(r.data || []);
    } catch (e) {
      toast.error('فشل تحميل سجل الدفعات');
    } finally { setLoading(false); }
  }, [flight.id, walkin.id]);

  useEffect(() => { reload(); }, [reload]);

  const uploadReceipt = async (paymentId, file) => {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('❌ السماح فقط بـ JPG / PNG / PDF'); return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('❌ الملف أكبر من 4 ميغا'); return;
    }
    setUploadingId(paymentId);
    try {
      const fd = new FormData();
      fd.append('document', file);
      await axios.post(
        `${API}/flights/walkins/payments/${paymentId}/upload-reference`,
        fd,
        { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('✅ تم رفع وثيقة الإثبات');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setUploadingId(null); }
  };

  const sale_price = walkin.sale_price || 0;
  const sale_paid = walkin.sale_paid || 0;
  const remaining = sale_price - sale_paid;
  const total_amount = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const total_profit = payments.reduce((s, p) => s + (p.profit_recognized || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-blue-900 flex items-center gap-2 flex-wrap">
            👁️ تفاصيل دفعات الراكب المباشر — {walkin.full_name}
          </DialogTitle>
          <DialogDescription>
            رحلة {flight?.flight_no} • {flight?.airline_name} • {flight?.departure_date}
            {walkin.buyer_type === 'subagent' && walkin.subagent_name && (
              <span className="block text-violet-700 mt-1">🏢 وكالة شريكة: {walkin.subagent_name}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-2">
            <div className="text-[10px] text-blue-700">🏷️ سعر البيع</div>
            <div className="text-base font-bold text-blue-800">{fmt(sale_price)} دج</div>
          </CardContent></Card>
          <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-2">
            <div className="text-[10px] text-emerald-700">💰 المدفوع</div>
            <div className="text-base font-bold text-emerald-800">{fmt(sale_paid)} دج</div>
            <div className="text-[10px] text-emerald-600">{payments.length} دفعة</div>
          </CardContent></Card>
          <Card className="bg-amber-50 border-amber-200"><CardContent className="p-2">
            <div className="text-[10px] text-amber-700">⏳ الباقي</div>
            <div className="text-base font-bold text-amber-800">{fmt(remaining)} دج</div>
            <div className="text-[10px] text-amber-600">{remaining > 0 ? 'غير مسدد' : '✅ مسدد'}</div>
          </CardContent></Card>
          <Card className="bg-violet-50 border-violet-200"><CardContent className="p-2">
            <div className="text-[10px] text-violet-700">💎 ربح للخزينة</div>
            <div className="text-base font-bold text-violet-800">{fmt(total_profit)} دج</div>
          </CardContent></Card>
        </div>

        {/* Payments history */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between flex-wrap gap-2">
              <span>💳 سجل الدفعات ({payments.length})</span>
              {remaining > 0 && onPay && (
                <Button size="sm" onClick={onPay} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="walkin-detail-add-payment">
                  <Plus className="h-3 w-3 ml-1" /> إضافة دفعة
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center text-gray-500 py-2 text-sm">⏳ جارٍ التحميل…</div>
            ) : payments.length === 0 ? (
              <div className="text-center text-gray-500 py-3 text-sm">
                لا توجد دفعات مسجلة بعد لهذه التذكرة.
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-right">#</TableHead>
                  <TableHead className="text-right">التاريخ</TableHead>
                  <TableHead className="text-right">المبلغ</TableHead>
                  <TableHead className="text-right">الطريقة</TableHead>
                  <TableHead className="text-right">المرجع</TableHead>
                  <TableHead className="text-right">الوثيقة</TableHead>
                  <TableHead className="text-right">ربح للخزينة</TableHead>
                  <TableHead className="text-right">المُستلِم</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payments.map((p, idx) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        {idx + 1}
                        {p.is_initial && <Badge className="bg-blue-100 text-blue-800 text-[10px] mr-1">أولى</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{(p.created_at || '').slice(0, 19).replace('T', ' ')}</TableCell>
                      <TableCell className="font-mono font-bold text-emerald-700">{fmt(p.amount)} دج</TableCell>
                      <TableCell><Badge className="bg-gray-100 text-gray-700 text-[10px]">{p.method || 'cash'}</Badge></TableCell>
                      <TableCell className="text-xs">{p.reference || '—'}</TableCell>
                      <TableCell>
                        {p.reference_document_url ? (
                          <a
                            href={`${API}${p.reference_document_url}?token=${localStorage.getItem('token')}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-emerald-700 underline hover:text-emerald-900 text-xs"
                            data-testid={`walkin-pay-doc-${p.id}`}
                            title="عرض الوثيقة"
                          >📎 وثيقة</a>
                        ) : (
                          <label className="cursor-pointer text-blue-600 hover:text-blue-800 text-xs underline" title="رفع وصل إثبات">
                            {uploadingId === p.id ? '⏳ جارٍ الرفع…' : '⬆️ رفع وصل'}
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                              className="hidden"
                              data-testid={`walkin-pay-upload-${p.id}`}
                              disabled={uploadingId === p.id}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadReceipt(p.id, f);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-blue-700">{p.profit_recognized > 0 ? fmt(p.profit_recognized) : '—'}</TableCell>
                      <TableCell className="text-xs">{p.created_by_name || '—'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-emerald-50 font-bold">
                    <TableCell colSpan={2}>المجموع</TableCell>
                    <TableCell className="font-mono text-emerald-800">{fmt(total_amount)} دج</TableCell>
                    <TableCell colSpan={3}></TableCell>
                    <TableCell className="font-mono text-blue-800">{fmt(total_profit)} دج</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
              💡 يمكنك رفع وصل/شيك/تحويل إثبات الدفع لأي دفعة لم تُرفع لها وثيقة بعد بالنقر على «⬆️ رفع وصل».
            </p>
          </CardContent>
        </Card>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



// ============================================================ Edit Subagent Block
const EditSubagentBlockDialog = ({ block, flight, details, onClose, onSaved }) => {
  const [form, setForm] = useState({
    seats_adult: block.seats_adult || 0,
    seats_child: block.seats_child || 0,
    seats_infant_with_seat: block.seats_infant_with_seat || 0,
    seats_infant_no_seat: block.seats_infant_no_seat || 0,
    total_price: block.total_price || 0,
    notes: block.notes || '',
  });

  const f = flight || {};
  const seatsTotal = (parseInt(form.seats_adult)||0) + (parseInt(form.seats_child)||0) + (parseInt(form.seats_infant_with_seat)||0);
  const oldSeats = (block.seats_adult||0) + (block.seats_child||0) + (block.seats_infant_with_seat||0);
  const blockCost =
    (parseInt(form.seats_adult)||0) * (f.price_adult_with_seat || 0)
    + (parseInt(form.seats_child)||0) * (f.price_child_with_seat || 0)
    + (parseInt(form.seats_infant_with_seat)||0) * (f.price_infant_with_seat || 0)
    + (parseInt(form.seats_infant_no_seat)||0) * (f.price_infant_no_seat || 0);
  const remainingSeats = (details?.stats?.remaining_seats ?? 0) + oldSeats; // we can re-claim our own seats

  const submit = async () => {
    if (seatsTotal > remainingSeats) {
      toast.error(`🚫 المقاعد غير كافية. الحد الأقصى: ${remainingSeats}`); return;
    }
    try {
      await axios.patch(`${API}/flights/subagent-blocks/${block.id}`, {
        seats_adult: parseInt(form.seats_adult)||0,
        seats_child: parseInt(form.seats_child)||0,
        seats_infant_with_seat: parseInt(form.seats_infant_with_seat)||0,
        seats_infant_no_seat: parseInt(form.seats_infant_no_seat)||0,
        total_price: parseFloat(form.total_price)||0,
        notes: form.notes,
      }, { headers: auth() });
      toast.success('✏️ تم تحديث الحجز'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>✏️ تعديل حجز كتلة وكالة شريكة</DialogTitle>
          <DialogDescription>
            🏢 {block.subagent_name} • الوكالة الشريكة مقفولة — يمكنك تعديل المقاعد والسعر والملاحظات فقط.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-violet-50 border border-violet-200 rounded p-3">
            <Label className="font-bold text-violet-900 block mb-2">المقاعد المحجوزة</Label>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">كبير <span className="font-mono text-violet-700">ADT</span></Label><Input type="number" value={form.seats_adult || ''} placeholder="0" onChange={e => setForm({...form, seats_adult: e.target.value})} data-testid="edit-block-seats-adt" /></div>
              <div><Label className="text-xs">طفل <span className="font-mono text-violet-700">CHD</span></Label><Input type="number" value={form.seats_child || ''} placeholder="0" onChange={e => setForm({...form, seats_child: e.target.value})} data-testid="edit-block-seats-chd" /></div>
              <div><Label className="text-xs">رضيع بكرسي <span className="font-mono text-violet-700">INS</span></Label><Input type="number" value={form.seats_infant_with_seat || ''} placeholder="0" onChange={e => setForm({...form, seats_infant_with_seat: e.target.value})} data-testid="edit-block-seats-ins" /></div>
              <div><Label className="text-xs">رضيع بدون كرسي <span className="font-mono text-violet-700">INF</span></Label><Input type="number" value={form.seats_infant_no_seat || ''} placeholder="0" onChange={e => setForm({...form, seats_infant_no_seat: e.target.value})} data-testid="edit-block-seats-inf" /></div>
            </div>
            <div className="text-xs text-violet-800 mt-2 bg-white rounded p-2 border border-violet-200">
              📊 إجمالي مقاعد: <strong>{seatsTotal}</strong> {(parseInt(form.seats_infant_no_seat)||0) > 0 && <span>+ {form.seats_infant_no_seat} رضيع بدون مقعد</span>} • أقصى مسموح: <strong>{remainingSeats}</strong>
            </div>
          </div>
          <div>
            <Label>السعر الإجمالي للحجز (دج) *</Label>
            <Input type="number" value={form.total_price || ''} placeholder="0" onChange={e => setForm({...form, total_price: e.target.value})} data-testid="edit-block-total-price" />
            <p className="text-[10px] text-amber-700 mt-1">⚠️ تغيير السعر بعد تسجيل دفعات يُعاد حساب الربح المتراكم تلقائياً عند الدفعة القادمة.</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded p-3 border border-emerald-200 text-xs">
            <div className="grid grid-cols-3 gap-2">
              <div><div className="text-gray-600">تكلفة الطيران</div><div className="font-bold text-rose-700">{fmt(blockCost)} دج</div></div>
              <div><div className="text-gray-600">المدفوع سابقاً</div><div className="font-bold text-emerald-700">{fmt(block.amount_paid || 0)} دج</div></div>
              <div><div className="text-gray-600">هامش الربح</div><div className={`font-bold ${(form.total_price - blockCost) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt((parseFloat(form.total_price)||0) - blockCost)} دج</div></div>
            </div>
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="edit-block-submit">
            💾 حفظ التعديلات
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Subagent Block — Full Details + Payments History
const SubagentBlockDetailsDialog = ({ block, flight, onClose, onPay }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fresh, setFresh] = useState(block);

  useEffect(() => {
    (async () => {
      try {
        const [pres, lres] = await Promise.all([
          axios.get(`${API}/flights/subagent-blocks/${block.id}/payments`, { headers: auth() }),
          axios.get(`${API}/flights/${flight.id}/subagent-blocks`, { headers: auth() }),
        ]);
        setPayments(pres.data || []);
        const found = (lres.data || []).find(x => x.id === block.id);
        if (found) setFresh(found);
      } catch (e) {
        toast.error('فشل تحميل التفاصيل');
      } finally { setLoading(false); }
    })();
  }, [block.id, flight.id]);

  const f = flight || {};
  const b = fresh;
  const sa = b.seats_adult || 0, sc = b.seats_child || 0;
  const sins = b.seats_infant_with_seat || 0, sinf = b.seats_infant_no_seat || 0;
  const cost_adt = f.price_adult_with_seat || 0;
  const cost_chd = f.price_child_with_seat || 0;
  const cost_ins = f.price_infant_with_seat || 0;
  const cost_inf = f.price_infant_no_seat || 0;
  const total_seats = sa + sc + sins + sinf;
  const seats_count_excl_inf = sa + sc + sins; // INF doesn't take a seat
  const total_cost = sa*cost_adt + sc*cost_chd + sins*cost_ins + sinf*cost_inf;
  const total_sale = b.total_price || 0;
  const total_paid = b.amount_paid || 0;
  const total_remaining = total_sale - total_paid;
  const profit_margin = total_sale - total_cost;
  const profit_collected = b.profit_collected || 0;
  const profit_pending = Math.max(0, profit_margin - profit_collected);
  const avg_sale_per_seat = total_seats > 0 ? total_sale / total_seats : 0;
  const avg_cost_per_seat = total_seats > 0 ? total_cost / total_seats : 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-violet-900 flex items-center gap-2 flex-wrap">
            🏢 تفاصيل حجز الوكالة الشريكة — {b.subagent_name}
          </DialogTitle>
          <DialogDescription>
            رحلة {f.flight_no} • {f.airline_name} • {f.departure_date}
            {b.notes && <span className="block text-gray-600 mt-1">📝 {b.notes}</span>}
          </DialogDescription>
        </DialogHeader>

        {/* Top KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          <Card className="bg-rose-50 border-rose-200"><CardContent className="p-2">
            <div className="text-[10px] text-rose-700">💸 السعر الأصلي (التكلفة)</div>
            <div className="text-base font-bold text-rose-800">{fmt(total_cost)} دج</div>
            <div className="text-[10px] text-rose-600">ما تدفعه للطيران</div>
          </CardContent></Card>
          <Card className="bg-blue-50 border-blue-200"><CardContent className="p-2">
            <div className="text-[10px] text-blue-700">🏷️ السعر المباع به</div>
            <div className="text-base font-bold text-blue-800">{fmt(total_sale)} دج</div>
            <div className="text-[10px] text-blue-600">المتفق عليه مع الوكالة</div>
          </CardContent></Card>
          <Card className="bg-emerald-50 border-emerald-200"><CardContent className="p-2">
            <div className="text-[10px] text-emerald-700">💎 الربح الصافي</div>
            <div className={`text-base font-bold ${profit_margin >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>{fmt(profit_margin)} دج</div>
            <div className="text-[10px] text-emerald-600">سعر البيع − التكلفة</div>
          </CardContent></Card>
          <Card className="bg-teal-50 border-teal-200"><CardContent className="p-2">
            <div className="text-[10px] text-teal-700">💰 المدفوع</div>
            <div className="text-base font-bold text-teal-800">{fmt(total_paid)} دج</div>
            <div className="text-[10px] text-teal-600">{payments.length} دفعة</div>
          </CardContent></Card>
          <Card className="bg-amber-50 border-amber-200"><CardContent className="p-2">
            <div className="text-[10px] text-amber-700">⏳ الباقي على الوكالة</div>
            <div className="text-base font-bold text-amber-800">{fmt(total_remaining)} دج</div>
            <div className="text-[10px] text-amber-600">{total_remaining > 0 ? 'لم يُسدد بعد' : '✅ مسدد كاملاً'}</div>
          </CardContent></Card>
        </div>

        {/* Detailed per-seat-type table */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">📊 تفصيل التكلفة لكل فئة مقاعد</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-right">الفئة</TableHead>
                <TableHead className="text-right">العدد</TableHead>
                <TableHead className="text-right">سعر الوحدة (الطيران)</TableHead>
                <TableHead className="text-right">المجموع</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sa > 0 && <TableRow><TableCell><Badge className="bg-blue-100 text-blue-800">كبير ADT</Badge></TableCell><TableCell className="font-bold">{sa}</TableCell><TableCell className="font-mono">{fmt(cost_adt)} دج</TableCell><TableCell className="font-mono">{fmt(sa*cost_adt)} دج</TableCell></TableRow>}
                {sc > 0 && <TableRow><TableCell><Badge className="bg-emerald-100 text-emerald-800">طفل CHD</Badge></TableCell><TableCell className="font-bold">{sc}</TableCell><TableCell className="font-mono">{fmt(cost_chd)} دج</TableCell><TableCell className="font-mono">{fmt(sc*cost_chd)} دج</TableCell></TableRow>}
                {sins > 0 && <TableRow><TableCell><Badge className="bg-sky-100 text-sky-800">رضيع بكرسي INS</Badge></TableCell><TableCell className="font-bold">{sins}</TableCell><TableCell className="font-mono">{fmt(cost_ins)} دج</TableCell><TableCell className="font-mono">{fmt(sins*cost_ins)} دج</TableCell></TableRow>}
                {sinf > 0 && <TableRow><TableCell><Badge className="bg-cyan-100 text-cyan-800">رضيع بدون كرسي INF</Badge></TableCell><TableCell className="font-bold">{sinf}</TableCell><TableCell className="font-mono">{fmt(cost_inf)} دج</TableCell><TableCell className="font-mono">{fmt(sinf*cost_inf)} دج</TableCell></TableRow>}
                <TableRow className="bg-rose-50 font-bold">
                  <TableCell colSpan={2}>إجمالي التكلفة ({total_seats} تذكرة، {seats_count_excl_inf} مقعد)</TableCell>
                  <TableCell className="text-xs text-gray-600">متوسط/مقعد: {fmt(avg_cost_per_seat)}</TableCell>
                  <TableCell className="font-mono text-rose-800">{fmt(total_cost)} دج</TableCell>
                </TableRow>
                <TableRow className="bg-blue-50 font-bold">
                  <TableCell colSpan={2}>السعر الإجمالي المباع للوكالة</TableCell>
                  <TableCell className="text-xs text-gray-600">متوسط/تذكرة: {fmt(avg_sale_per_seat)}</TableCell>
                  <TableCell className="font-mono text-blue-800">{fmt(total_sale)} دج</TableCell>
                </TableRow>
                <TableRow className="bg-emerald-50 font-bold">
                  <TableCell colSpan={3}>هامش الربح الصافي</TableCell>
                  <TableCell className={`font-mono ${profit_margin >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>{fmt(profit_margin)} دج</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                <div className="text-emerald-700">💎 ربح وصل للخزينة</div>
                <div className="font-bold text-emerald-900">{fmt(profit_collected)} دج</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <div className="text-amber-700">🕒 ربح متبقي للتحصيل</div>
                <div className="font-bold text-amber-900">{fmt(profit_pending)} دج</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payments history */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center justify-between flex-wrap gap-2">
            <span>💳 سجل الدفعات ({payments.length})</span>
            {total_remaining > 0 && onPay && (
              <Button size="sm" onClick={onPay} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="detail-add-payment">
                <Plus className="h-3 w-3 ml-1" /> إضافة دفعة
              </Button>
            )}
          </CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center text-gray-500 py-2 text-sm">⏳ جارٍ التحميل…</div>
            ) : payments.length === 0 ? (
              <div className="text-center text-gray-500 py-3 text-sm">
                لا توجد دفعات مسجلة بعد {total_paid > 0 && <span>(الدفعة الأولى كانت عند إنشاء الحجز: {fmt(total_paid)} دج)</span>}
              </div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="text-right">#</TableHead>
                  <TableHead className="text-right">التاريخ</TableHead>
                  <TableHead className="text-right">المبلغ</TableHead>
                  <TableHead className="text-right">الطريقة</TableHead>
                  <TableHead className="text-right">المرجع</TableHead>
                  <TableHead className="text-right">الوثيقة</TableHead>
                  <TableHead className="text-right">ربح للخزينة</TableHead>
                  <TableHead className="text-right">المُستلِم</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {payments.map((p, idx) => (
                    <TableRow key={p.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell className="text-xs">{(p.created_at || '').slice(0, 19).replace('T', ' ')}</TableCell>
                      <TableCell className="font-mono font-bold text-emerald-700">{fmt(p.amount)} دج</TableCell>
                      <TableCell><Badge className="bg-gray-100 text-gray-700 text-[10px]">{p.method || 'cash'}</Badge></TableCell>
                      <TableCell className="text-xs">{p.reference || '—'}</TableCell>
                      <TableCell>
                        {p.reference_document_url ? (
                          <a
                            href={`${API}${p.reference_document_url}?token=${localStorage.getItem('token')}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-emerald-700 underline hover:text-emerald-900 text-xs"
                            data-testid={`block-pay-doc-${p.id}`}
                            title="عرض وثيقة الإثبات"
                          >📎 وثيقة</a>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="font-mono text-blue-700">{p.profit_recognized > 0 ? fmt(p.profit_recognized) : '—'}</TableCell>
                      <TableCell className="text-xs">{p.created_by_name || '—'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-emerald-50 font-bold">
                    <TableCell colSpan={2}>المجموع</TableCell>
                    <TableCell className="font-mono text-emerald-800">{fmt(payments.reduce((s, p) => s + (p.amount || 0), 0))} دج</TableCell>
                    <TableCell colSpan={3}></TableCell>
                    <TableCell className="font-mono text-blue-800">{fmt(payments.reduce((s, p) => s + (p.profit_recognized || 0), 0))} دج</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Confirm Delete Block (modal بديل أوضح من window.confirm)
const ConfirmDeleteBlockDialog = ({ block, onClose, onConfirm }) => {
  const profit = block.profit_collected || 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-rose-700">🗑️ تأكيد حذف الحجز</DialogTitle>
          <DialogDescription>هذا الإجراء لا يمكن التراجع عنه</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm">
            <div className="font-bold text-rose-900 mb-2">🏢 {block.subagent_name}</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-600">المقاعد:</span> <strong>{block.seats_total}</strong></div>
              <div><span className="text-gray-600">السعر:</span> <strong>{fmt(block.total_price)} دج</strong></div>
              <div><span className="text-gray-600">المدفوع:</span> <strong className="text-emerald-700">{fmt(block.amount_paid || 0)} دج</strong></div>
              <div><span className="text-gray-600">الباقي:</span> <strong className="text-rose-700">{fmt(block.amount_remaining || 0)} دج</strong></div>
            </div>
            {profit > 0 && (
              <div className="mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                ⚠️ سيتم استرجاع <strong>{fmt(profit)} دج</strong> من الخزينة (الأرباح المتراكمة).
              </div>
            )}
            {(block.names_count || 0) > 0 && (
              <div className="mt-2 text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 text-xs">
                📛 سيتم حذف <strong>{block.names_count}</strong> اسم/أسماء مرتبطة بهذا الحجز.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="confirm-delete-block-cancel">إلغاء</Button>
          <Button onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-block-confirm">
            <Trash2 className="h-3 w-3 ml-1" /> حذف نهائياً
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Subagent Block Booking (حجز كتلة وكالة شريكة)
const AddSubagentBlockDialog = ({ flight, details, onClose, onAdded }) => {
  const [subagents, setSubagents] = useState([]);
  const [form, setForm] = useState({
    subagent_id: '',
    seats_adult: 0, seats_child: 0, seats_infant_with_seat: 0, seats_infant_no_seat: 0,
    total_price: 0, amount_paid: 0, notes: '',
  });

  useEffect(() => {
    axios.get(`${API}/flights/meta/subagents`, { headers: auth() })
      .then(r => setSubagents(r.data || []))
      .catch(() => {});
  }, []);

  const f = flight || {};
  const seatsTotal = (parseInt(form.seats_adult)||0) + (parseInt(form.seats_child)||0) + (parseInt(form.seats_infant_with_seat)||0);
  const inf = parseInt(form.seats_infant_no_seat) || 0;
  const blockCost =
    (parseInt(form.seats_adult)||0) * (f.price_adult_with_seat || 0)
    + (parseInt(form.seats_child)||0) * (f.price_child_with_seat || 0)
    + (parseInt(form.seats_infant_with_seat)||0) * (f.price_infant_with_seat || 0)
    + inf * (f.price_infant_no_seat || 0);
  const remaining = (parseFloat(form.total_price)||0) - (parseFloat(form.amount_paid)||0);
  const remainingSeats = details?.stats?.remaining_seats ?? 0;

  const submit = async () => {
    if (!form.subagent_id) { toast.error('❌ يجب اختيار الوكالة الشريكة'); return; }
    if (seatsTotal <= 0 && inf <= 0) { toast.error('❌ يجب إدخال عدد المقاعد'); return; }
    if (!form.total_price || form.total_price <= 0) { toast.error('❌ السعر الإجمالي إلزامي'); return; }
    if (seatsTotal > remainingSeats) {
      toast.error(`🚫 المقاعد غير كافية. المتبقي: ${remainingSeats}`); return;
    }
    if ((parseFloat(form.amount_paid)||0) > (parseFloat(form.total_price)||0)) {
      toast.error('❌ المدفوع لا يمكن أن يتجاوز السعر الإجمالي'); return;
    }
    try {
      await axios.post(`${API}/flights/${flight.id}/subagent-blocks`, {
        ...form,
        seats_adult: parseInt(form.seats_adult)||0,
        seats_child: parseInt(form.seats_child)||0,
        seats_infant_with_seat: parseInt(form.seats_infant_with_seat)||0,
        seats_infant_no_seat: parseInt(form.seats_infant_no_seat)||0,
        total_price: parseFloat(form.total_price)||0,
        amount_paid: parseFloat(form.amount_paid)||0,
      }, { headers: auth() });
      toast.success('✅ تم إنشاء حجز الوكالة الشريكة');
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>🏢 حجز كتلة لوكالة شريكة</DialogTitle>
          <DialogDescription>
            احجز عدداً من المقاعد لصالح وكالة شريكة. المقاعد ستُخصم تلقائياً من إجمالي الرحلة.
            الأسماء ليست إلزامية — يمكن إضافتها لاحقاً إن وُجدت.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="font-bold">الوكالة الشريكة *</Label>
            <select
              value={form.subagent_id}
              onChange={e => setForm({...form, subagent_id: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              data-testid="block-subagent-select"
            >
              <option value="">— اختر الوكالة —</option>
              {subagents.map(s => (
                <option key={s.id} value={s.id}>
                  {s.business_name || s.name} {s.tier ? `(${s.tier})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded p-3">
            <Label className="font-bold text-violet-900 block mb-2">المقاعد المحجوزة</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">كبير <span className="font-mono text-violet-700">ADT</span></Label>
                <Input type="number" value={form.seats_adult || ''} placeholder="0" onChange={e => setForm({...form, seats_adult: e.target.value})} data-testid="block-seats-adt" />
              </div>
              <div>
                <Label className="text-xs">طفل <span className="font-mono text-violet-700">CHD</span></Label>
                <Input type="number" value={form.seats_child || ''} placeholder="0" onChange={e => setForm({...form, seats_child: e.target.value})} data-testid="block-seats-chd" />
              </div>
              <div>
                <Label className="text-xs">رضيع بكرسي <span className="font-mono text-violet-700">INS</span></Label>
                <Input type="number" value={form.seats_infant_with_seat || ''} placeholder="0" onChange={e => setForm({...form, seats_infant_with_seat: e.target.value})} data-testid="block-seats-ins" />
              </div>
              <div>
                <Label className="text-xs">رضيع بدون كرسي <span className="font-mono text-violet-700">INF</span></Label>
                <Input type="number" value={form.seats_infant_no_seat || ''} placeholder="0" onChange={e => setForm({...form, seats_infant_no_seat: e.target.value})} data-testid="block-seats-inf" />
              </div>
            </div>
            <div className="text-xs text-violet-800 mt-2 bg-white rounded p-2 border border-violet-200">
              📊 إجمالي مقاعد يأخذها الحجز: <strong>{seatsTotal}</strong> {inf > 0 && <span>+ {inf} رضيع بدون مقعد</span>} • متبقي على الرحلة: <strong>{remainingSeats}</strong>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>السعر الإجمالي للحجز (دج) *</Label>
              <Input type="number" value={form.total_price || ''} placeholder="0" onChange={e => setForm({...form, total_price: e.target.value})} data-testid="block-total-price" />
            </div>
            <div>
              <Label>المدفوع الآن (دج)</Label>
              <Input type="number" value={form.amount_paid || ''} placeholder="0 (بيع على دين)" onChange={e => setForm({...form, amount_paid: e.target.value})} data-testid="block-amount-paid" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded p-3 border border-emerald-200 text-xs">
            <div className="grid grid-cols-3 gap-2">
              <div><div className="text-gray-600">تكلفة الطيران</div><div className="font-bold text-rose-700">{fmt(blockCost)} دج</div></div>
              <div><div className="text-gray-600">هامش الربح</div><div className={`font-bold ${(form.total_price - blockCost) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt((parseFloat(form.total_price)||0) - blockCost)} دج</div></div>
              <div><div className="text-gray-600">الباقي على الوكالة</div><div className="font-bold text-blue-700">{fmt(remaining)} دج</div></div>
            </div>
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="مثال: حجز خاص لعيد الأضحى" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="block-submit">
            💾 إنشاء الحجز
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Pay Subagent Block
const PaySubagentBlockDialog = ({ block, onClose, onPaid }) => {
  const [form, setForm] = useState({ amount: 0, method: 'cash', reference: '', notes: '' });
  const [docFile, setDocFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const remaining = (block.total_price || 0) - (block.amount_paid || 0);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setDocFile(null); return; }
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(f.type)) {
      toast.error('❌ السماح فقط بـ JPG / PNG / PDF');
      e.target.value = ''; setDocFile(null); return;
    }
    if (f.size > 4 * 1024 * 1024) {
      toast.error('❌ الملف أكبر من 4 ميغا'); e.target.value = ''; setDocFile(null); return;
    }
    setDocFile(f);
  };

  const submit = async () => {
    const amt = parseFloat(form.amount) || 0;
    if (amt <= 0) { toast.error('❌ المبلغ يجب أن يكون موجباً'); return; }
    if (amt > remaining + 0.01) { toast.error(`❌ المبلغ يتجاوز الباقي (${fmt(remaining)} دج)`); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/flights/subagent-blocks/${block.id}/pay`,
        { ...form, amount: amt }, { headers: auth() });
      const pd = res.data?.profit_delta || 0;
      const paymentId = res.data?.payment_id;
      // Upload doc if provided
      if (docFile && paymentId) {
        const fd = new FormData();
        fd.append('document', docFile);
        try {
          await axios.post(
            `${API}/flights/subagent-blocks/payments/${paymentId}/upload-reference`,
            fd,
            { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } },
          );
        } catch (upErr) {
          toast.error('⚠️ الدفعة سُجلت لكن فشل رفع الوثيقة: ' + (upErr?.response?.data?.detail || upErr.message));
        }
      }
      toast.success(`✅ تم تسجيل دفعة ${fmt(amt)} دج في الخزينة${pd > 0 ? ` • ربح صافٍ: ${fmt(pd)} دج` : ''}${docFile ? ' • مع وثيقة الإثبات' : ''}`);
      onPaid();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSubmitting(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-md">
        <DialogHeader>
          <DialogTitle>💰 تسديد دفعة من وكالة شريكة</DialogTitle>
          <DialogDescription>🏢 {block.subagent_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-gray-50 rounded p-2 text-xs grid grid-cols-3 gap-2">
            <div><div className="text-gray-500">السعر</div><div className="font-bold text-blue-700">{fmt(block.total_price)} دج</div></div>
            <div><div className="text-gray-500">المدفوع</div><div className="font-bold text-emerald-700">{fmt(block.amount_paid || 0)} دج</div></div>
            <div><div className="text-gray-500">الباقي</div><div className="font-bold text-rose-700">{fmt(remaining)} دج</div></div>
          </div>
          <div>
            <Label>المبلغ (دج) *</Label>
            <Input type="number" value={form.amount || ''} placeholder="0" onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} data-testid="block-pay-amount" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>طريقة الدفع</Label>
              <select value={form.method} onChange={e => setForm({...form, method: e.target.value})} className="w-full px-3 py-2 border rounded-md">
                <option value="cash">نقداً</option>
                <option value="transfer">حوالة</option>
                <option value="check">شيك</option>
                <option value="card">بطاقة</option>
              </select>
            </div>
            <div>
              <Label>رقم المرجع</Label>
              <Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} placeholder="REC-..." />
            </div>
          </div>
          <div>
            <Label>📎 وثيقة الإثبات (اختياري)</Label>
            <Input
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              onChange={onPickFile}
              data-testid="block-pay-document"
              className="cursor-pointer"
            />
            <p className="text-[10px] text-gray-500 mt-1">JPG / PNG / PDF — حتى 4 ميغا. ستظهر الوثيقة في سجل الدفعات للمراجعة لاحقاً.</p>
            {docFile && (
              <div className="mt-1 text-xs bg-blue-50 border border-blue-200 rounded p-1.5 text-blue-800">
                ✅ مُختار: <strong>{docFile.name}</strong> ({(docFile.size / 1024).toFixed(0)} KB)
              </div>
            )}
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="block-pay-submit">
            {submitting ? '⏳ جارٍ الحفظ…' : '💾 تسجيل الدفعة'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Add Payment Milestone (دفعة طيران مجدولة)
const AddMilestoneDialog = ({ flight, onClose, onAdded }) => {
  const [form, setForm] = useState({
    label: 'الدفعة الأولى',
    amount: 0,
    due_date: '',
    notes: '',
  });
  const PRESET_LABELS = ['الدفعة الأولى', 'الدفعة الثانية', 'الدفعة الثالثة', 'الدفعة النهائية', 'دفعة إضافية'];

  const submit = async () => {
    if (!form.label || !form.amount || !form.due_date) {
      toast.error('❌ التسمية + القيمة + تاريخ الاستحقاق إلزامية');
      return;
    }
    try {
      await axios.post(`${API}/flights/${flight.id}/payment-milestones`, form, { headers: auth() });
      toast.success('✅ تمت إضافة الدفعة المجدولة'); onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>📅 إضافة دفعة طيران مجدولة</DialogTitle>
          <DialogDescription>
            أدخل القيد الذي عاطيك إياه شركة الطيران. السستم يذكّرك تلقائياً في جرس الإشعارات قبل الموعد (تبويب 💳 دفعات طيران).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>التسمية *</Label>
            <select
              value={form.label}
              onChange={e => setForm({...form, label: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              data-testid="milestone-form-label"
            >
              {PRESET_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">يمكنك تعديل التسمية يدوياً بالأسفل إذا أردت</p>
            <Input
              value={form.label}
              onChange={e => setForm({...form, label: e.target.value})}
              className="mt-1"
              placeholder="أو اكتب تسمية مخصصة"
            />
          </div>
          <div>
            <Label>القيمة (دج) *</Label>
            <Input
              type="number"
              value={form.amount || ''}
              placeholder="0"
              onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})}
              data-testid="milestone-form-amount"
            />
          </div>
          <div>
            <Label>تاريخ الاستحقاق *</Label>
            <Input
              type="date"
              value={form.due_date}
              onChange={e => setForm({...form, due_date: e.target.value})}
              data-testid="milestone-form-due-date"
            />
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Input
              value={form.notes}
              onChange={e => setForm({...form, notes: e.target.value})}
              placeholder="مثلاً: حسب اتفاق X مع الجزائرية"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="milestone-form-submit">
            إضافة
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Add Walk-in
const AddWalkinDialog = ({ flight, onClose, onAdded }) => {
  const [form, setForm] = useState({
    seat_type: 'adult_with_seat',
    full_name: '', cin: '', passport_no: '', phone: '', birth_date: '',
    pnr: '', ticket_number: '', crew_role: '', discount_category: '', notes: '',
    // 🆕 Sales fields
    sale_price: 0, sale_paid: 0, buyer_type: 'individual', subagent_id: '',
  });
  const [subagents, setSubagents] = useState([]);
  const [docFile, setDocFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isCrew = form.seat_type === 'crew_free';
  const isDiscounted = form.seat_type === 'discounted';
  const isFreeSeat = isCrew; // crew is free for client
  const seatDef = SEAT_TYPES.find(s => s.v === form.seat_type);
  const tierPrice = (isCrew ? 0 : (flight?.[seatDef?.priceField] || 0));
  const salePrice = parseFloat(form.sale_price) || 0;
  const salePaid = parseFloat(form.sale_paid) || 0;
  const profitMargin = salePrice - tierPrice;
  const profitToCashbox = Math.max(0, salePaid - tierPrice);

  useEffect(() => {
    if (form.buyer_type === 'subagent' && subagents.length === 0) {
      axios.get(`${API}/flights/meta/subagents`, { headers: auth() })
        .then(r => setSubagents(r.data || []))
        .catch(() => {});
    }
  }, [form.buyer_type, subagents.length]);

  const submit = async () => {
    if (!form.full_name) { toast.error('❌ اسم الراكب إلزامي'); return; }
    if (isCrew && !form.crew_role) { toast.error('❌ يجب تحديد دور الطاقم'); return; }
    if (isDiscounted && !form.discount_category) { toast.error('❌ يجب تحديد فئة المستفيد من التخفيض'); return; }
    if (form.buyer_type === 'subagent' && !form.subagent_id) {
      toast.error('❌ يجب اختيار الوكالة الشريكة'); return;
    }
    if (!isFreeSeat && salePrice > 0 && salePrice < tierPrice) {
      if (!window.confirm(`⚠️ تنبيه: سعر البيع (${fmt(salePrice)}) أقل من تكلفة الطيران (${fmt(tierPrice)}) — سيكون لديك خسارة ${fmt(tierPrice - salePrice)} دج. هل تريد المتابعة؟`)) return;
    }
    if (salePaid > salePrice && salePrice > 0) {
      toast.error('❌ المبلغ المدفوع لا يمكن أن يتجاوز سعر البيع'); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/flights/${flight.id}/walkins`, {
        ...form,
        sale_price: salePrice,
        sale_paid: salePaid,
      }, { headers: auth() });
      const created = res.data || {};
      const initialPaymentId = created.initial_payment_id;
      // 🆕 ارفع وصل الإثبات للدفعة الأولى إن وُجد
      if (docFile && initialPaymentId) {
        const fd = new FormData();
        fd.append('document', docFile);
        try {
          await axios.post(
            `${API}/flights/walkins/payments/${initialPaymentId}/upload-reference`,
            fd,
            { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } },
          );
        } catch (upErr) {
          toast.error('⚠️ التذكرة سُجلت لكن فشل رفع الوثيقة: ' + (upErr?.response?.data?.detail || upErr.message));
        }
      }
      toast.success(`✅ تم إضافة الراكب المباشر${salePaid > 0 ? ` • دخل ${fmt(salePaid)} دج للخزينة (ربح صافٍ ${fmt(profitToCashbox)} دج)` : ''}${docFile ? ' • مع وثيقة الإثبات' : ''}`);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setDocFile(null); return; }
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowed.includes(f.type)) {
      toast.error('❌ السماح فقط بـ JPG / PNG / PDF');
      e.target.value = ''; setDocFile(null); return;
    }
    if (f.size > 4 * 1024 * 1024) {
      toast.error('❌ الملف أكبر من 4 ميغا'); e.target.value = ''; setDocFile(null); return;
    }
    setDocFile(f);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>➕ إضافة راكب مباشر (Walk-in)</DialogTitle>
          <DialogDescription>
            للحالات التي تبيع فيها مقعداً بدون برنامج. تكلفة الطيران ستُحسب تلقائياً من جدول الأسعار.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>نوع المقعد *</Label>
            <select value={form.seat_type} onChange={e => setForm({...form, seat_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md" data-testid="walkin-seat-type">
              {SEAT_TYPES.map(s => (
                <option key={s.v} value={s.v}>
                  {s.label} {flight?.[s.priceField] ? `(تكلفة: ${fmt(flight[s.priceField])} دج)` : ''}
                </option>
              ))}
            </select>
            <div className="text-xs text-gray-600 mt-1">
              💰 تكلفة الطيران لهذا الراكب: <strong>{fmt(tierPrice)} دج</strong>
            </div>
            {form.seat_type === 'infant_with_seat' && (
              <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded p-2 mt-2">
                💡 <strong>متى نختار «رضيع بكرسي (INS)»؟</strong> عندما يكون في نفس العائلة <strong>رضيعان</strong> — الأول يجلس في الحضن (INF) والثاني يحتاج كرسياً مستقلاً (INS). كل بالغ مسموح له بحمل رضيع واحد فقط.
              </div>
            )}
          </div>
          <div className="col-span-2"><Label>الاسم الكامل *</Label><Input value={form.full_name} onChange={e=>setForm({...form, full_name: e.target.value})} data-testid="walkin-name" /></div>
          <div><Label>CIN</Label><Input value={form.cin} onChange={e=>setForm({...form, cin: e.target.value})} data-testid="walkin-cin" /></div>
          <div><Label>رقم الجواز</Label><Input value={form.passport_no} onChange={e=>setForm({...form, passport_no: e.target.value})} /></div>
          <div><Label>الهاتف</Label><Input value={form.phone} onChange={e=>setForm({...form, phone: e.target.value})} /></div>
          <div><Label>تاريخ الميلاد</Label><Input type="date" value={form.birth_date} onChange={e=>setForm({...form, birth_date: e.target.value})} /></div>
          {isCrew && (
            <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-2">
              <Label className="font-bold text-amber-900">الدور في الوكالة *</Label>
              <select value={form.crew_role} onChange={e=>setForm({...form, crew_role: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md mt-1" data-testid="walkin-crew-role">
                <option value="">— اختر الدور —</option>
                <option value="مرشد">مرشد</option>
                <option value="طبيب">طبيب</option>
                <option value="مرافق">مرافق</option>
                <option value="إداري">إداري</option>
                <option value="مدير">مدير</option>
              </select>
            </div>
          )}
          {isDiscounted && (
            <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded p-2">
              <Label className="font-bold text-emerald-900">فئة المستفيد من تخفيض الطيران *</Label>
              <select value={form.discount_category} onChange={e=>setForm({...form, discount_category: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md mt-1" data-testid="walkin-discount-category">
                <option value="">— اختر الفئة —</option>
                {DISCOUNT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <p className="text-xs text-emerald-700 mt-1">سيتم احتساب التكلفة بالسعر المخفّض المسجل على الرحلة.</p>
            </div>
          )}
          {!isCrew && !isDiscounted && (
            <>
              <div><Label>PNR</Label><Input value={form.pnr} onChange={e=>setForm({...form, pnr: e.target.value})} /></div>
              <div><Label>رقم التذكرة</Label><Input value={form.ticket_number} onChange={e=>setForm({...form, ticket_number: e.target.value})} /></div>
            </>
          )}
          <div className="col-span-2"><Label>ملاحظات</Label><Input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} /></div>

          {/* 🆕 Sales & buyer-type section */}
          {!isFreeSeat && (
            <div className="col-span-2 border-t pt-3 mt-2 bg-gradient-to-br from-emerald-50 to-teal-50 rounded p-3 border border-emerald-200">
              <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">💵 البيع و الربح</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="font-bold">نوع المشتري *</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button"
                      onClick={() => setForm({...form, buyer_type: 'individual', subagent_id: ''})}
                      className={`flex-1 px-3 py-2 rounded border-2 text-sm font-semibold transition-all ${form.buyer_type === 'individual' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-400'}`}
                      data-testid="walkin-buyer-individual">
                      👤 زبون فرد
                    </button>
                    <button type="button"
                      onClick={() => setForm({...form, buyer_type: 'subagent'})}
                      className={`flex-1 px-3 py-2 rounded border-2 text-sm font-semibold transition-all ${form.buyer_type === 'subagent' ? 'bg-violet-600 text-white border-violet-700' : 'bg-white border-gray-300 text-gray-700 hover:border-violet-400'}`}
                      data-testid="walkin-buyer-subagent">
                      🏢 وكالة شريكة
                    </button>
                  </div>
                </div>
                {form.buyer_type === 'subagent' && (
                  <div className="col-span-2">
                    <Label>الوكالة الشريكة *</Label>
                    <select
                      value={form.subagent_id}
                      onChange={e => setForm({...form, subagent_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md"
                      data-testid="walkin-subagent-select"
                    >
                      <option value="">— اختر الوكالة الشريكة —</option>
                      {subagents.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.business_name || s.name} {s.tier ? `(${s.tier})` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-violet-700 mt-1">
                      🔐 المتابعة المالية لهذه التذكرة تظهر للإدارة المركزية فقط — تتبّع التسديد والباقي.
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-xs">سعر البيع (دج) *</Label>
                  <Input type="number"
                    value={form.sale_price || ''}
                    placeholder="0"
                    onChange={e => setForm({...form, sale_price: parseFloat(e.target.value) || 0})}
                    data-testid="walkin-sale-price"
                  />
                </div>
                <div>
                  <Label className="text-xs">المُحصَّل الآن (دج)</Label>
                  <Input type="number"
                    value={form.sale_paid || ''}
                    placeholder={form.buyer_type === 'subagent' ? '0 (بيع على دين)' : '0'}
                    onChange={e => setForm({...form, sale_paid: parseFloat(e.target.value) || 0})}
                    data-testid="walkin-sale-paid"
                  />
                </div>
                <div className="col-span-2 bg-white rounded p-2 border border-emerald-300 text-xs grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-gray-500">تكلفة الطيران</div>
                    <div className="font-bold text-rose-700">{fmt(tierPrice)} دج</div>
                  </div>
                  <div>
                    <div className="text-gray-500">هامش الربح</div>
                    <div className={`font-bold ${profitMargin >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(profitMargin)} دج</div>
                  </div>
                  <div>
                    <div className="text-gray-500">↗ إلى الخزينة الآن</div>
                    <div className="font-bold text-blue-700">{fmt(profitToCashbox)} دج</div>
                  </div>
                </div>
                {form.buyer_type === 'subagent' && salePaid < salePrice && (
                  <div className="col-span-2 bg-violet-50 border border-violet-200 rounded p-2 text-xs text-violet-800">
                    💡 الباقي على الوكالة الشريكة: <strong>{fmt(salePrice - salePaid)} دج</strong>. يمكن للإدارة المركزية تسجيل دفعات لاحقة من قائمة الركاب بزر «تسديد».
                  </div>
                )}
                {salePaid > 0 && (
                  <div className="col-span-2">
                    <Label className="text-xs">📎 وثيقة إثبات الدفع الأولى (اختياري)</Label>
                    <Input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={onPickFile}
                      data-testid="walkin-initial-document"
                      className="cursor-pointer mt-1"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">JPG / PNG / PDF — حتى 4 ميغا. ستظهر في سجل دفعات هذه التذكرة.</p>
                    {docFile && (
                      <div className="mt-1 text-xs bg-blue-50 border border-blue-200 rounded p-1.5 text-blue-800">
                        ✅ مُختار: <strong>{docFile.name}</strong> ({(docFile.size / 1024).toFixed(0)} KB)
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button onClick={submit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="walkin-submit">
            {submitting ? '⏳ جارٍ الحفظ…' : 'إضافة'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================ Add Airline Payment
const AddAirlinePaymentDialog = ({ flight, onClose, onAdded }) => {
  const [form, setForm] = useState({
    amount: '', payment_date: new Date().toISOString().slice(0, 10),
    method: 'cash', reference: '', notes: '',
  });
  const [referenceFile, setReferenceFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('❌ المبلغ إلزامي وأكبر من الصفر');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/flights/${flight.id}/airline-payments`, {
        ...form, amount: parseFloat(form.amount),
      }, { headers: auth() });
      const payment = res.data;
      // 🆕 Upload the reference document if provided
      if (referenceFile && payment?.id) {
        try {
          const fd = new FormData();
          fd.append('document', referenceFile);
          await axios.post(
            `${API}/flights/${flight.id}/airline-payments/${payment.id}/upload-reference`,
            fd,
            { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } },
          );
        } catch (upErr) {
          toast.warning('⚠️ تم تسجيل الدفعة لكن فشل رفع الوثيقة — يمكنك رفعها لاحقاً');
        }
      }
      const newBal = payment?.cashbox_balance_after;
      toast.success(
        `✅ تم تسجيل الدفعة${newBal !== undefined ? ` — الرصيد الجديد للخزينة: ${Number(newBal).toLocaleString()} دج` : ''}`,
      );
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-md">
        <DialogHeader>
          <DialogTitle>💸 دفعة لشركة الطيران</DialogTitle>
          <DialogDescription>تسجيل ما دفعته الوكالة لشركة الطيران لهذه الرحلة.</DialogDescription>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
          ⚠️ <strong>تنبيه:</strong> هذه الدفعة ستُخصم تلقائياً من <strong>الرصيد الحقيقي للخزينة</strong> (الصندوق الرئيسي).
          عند الحذف يُعاد المبلغ كاملاً للخزينة.
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div><Label>المبلغ * (دج)</Label><Input type="number" value={form.amount} onChange={e=>setForm({...form, amount: e.target.value})} data-testid="pay-amount" /></div>
          <div><Label>التاريخ</Label><Input type="date" value={form.payment_date} onChange={e=>setForm({...form, payment_date: e.target.value})} /></div>
          <div>
            <Label>طريقة الدفع</Label>
            <select value={form.method} onChange={e => setForm({...form, method: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md">
              <option value="cash">نقداً</option>
              <option value="bank_transfer">تحويل بنكي</option>
              <option value="check">شيك</option>
              <option value="ccp">CCP</option>
            </select>
          </div>
          <div><Label>المرجع (رقم الوصل/الشيك)</Label><Input value={form.reference} onChange={e=>setForm({...form, reference: e.target.value})} data-testid="pay-reference" /></div>
          <div className="bg-blue-50 border border-blue-200 rounded p-2">
            <Label className="text-xs font-bold text-blue-900">📎 صورة المرجع (وصل / شيك / تحويل) — JPG/PNG/PDF ≤ 4MB</Label>
            <Input
              type="file"
              accept="image/jpeg,image/png,image/jpg,application/pdf"
              onChange={(e) => setReferenceFile(e.target.files?.[0] || null)}
              data-testid="pay-reference-document"
              className="text-xs mt-1"
            />
            {referenceFile && (
              <div className="text-xs text-blue-700 mt-1">✅ مُحدّد: {referenceFile.name}</div>
            )}
          </div>
          <div><Label>ملاحظات</Label><Input value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>إلغاء</Button>
          <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-submit">
            {submitting ? 'جاري الحفظ…' : 'تسجيل الدفعة'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FlightsManagement;
